import React, { useState, useEffect } from 'react';
import { adminGetSetting, adminSaveSetting } from '../../api/client';
import { Save, Plus, Trash2, Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

const sectionStyle = { background: 'var(--card)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 4, padding: 28, marginBottom: 20 };
const labelStyle = { fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600, color: '#6a6a88', marginBottom: 8, display: 'block' };
const inputStyle = { background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)', color: '#f2f2ff', fontFamily: "'DM Sans', sans-serif", fontSize: 14, borderRadius: 3 };

const DEFAULT_PROFILE = {
  name: 'Erwin Paisal', title: 'IT Professional',
  tagline: 'Solusi teknologi terpercaya untuk bisnis dan individu di Medan, Indonesia.',
  location: 'Medan, Sumatera Utara, Indonesia',
  email: 'erwin@erwinpaisal.id', phone: '+62 812-3456-7890',
  whatsapp: '6281234567890', linkedin: 'https://linkedin.com/in/erwinpaisal',
  photoUrl: '',
  bio: ['Halo! Saya Erwin Paisal, IT Professional berpengalaman di Medan.', 'Spesialis hardware, software, dan networking.'],
  strengths: ['Respon cepat', 'Solusi efisien', 'Jaminan kualitas', 'Pengalaman lintas industri'],
  experience: [
    { role: 'IT Support Specialist', company: 'PT. Teknologi Maju', period: '2021 – Sekarang', description: 'Mengelola infrastruktur IT perusahaan.' },
  ],
  stats: [{ label: 'Tahun Pengalaman', value: '5+' }, { label: 'Proyek Selesai', value: '80+' }, { label: 'Klien Puas', value: '60+' }, { label: 'Sertifikasi', value: '6' }],
  typingWords: ['Hardware Expert', 'Software Specialist', 'Network Engineer', 'IT Professional'],
};

export default function ProfileManager() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newExp, setNewExp] = useState({ role: '', company: '', period: '', description: '' });
  const [newStrength, setNewStrength] = useState('');
  const [newWord, setNewWord] = useState('');

  useEffect(() => {
    adminGetSetting('profile').then(data => {
      setProfile(data || DEFAULT_PROFILE);
    }).catch(() => setProfile(DEFAULT_PROFILE));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await adminSaveSetting('profile', profile); toast.success('Profil berhasil disimpan!'); }
    catch (e) { toast.error('Gagal: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const addExperience = () => {
    if (!newExp.role || !newExp.company) return;
    setProfile(p => ({ ...p, experience: [...(p.experience || []), newExp] }));
    setNewExp({ role: '', company: '', period: '', description: '' });
  };
  const removeExperience = (i) => setProfile(p => ({ ...p, experience: p.experience.filter((_, idx) => idx !== i) }));
  const addStrength = () => { if (!newStrength) return; setProfile(p => ({ ...p, strengths: [...(p.strengths || []), newStrength] })); setNewStrength(''); };
  const removeStrength = (i) => setProfile(p => ({ ...p, strengths: p.strengths.filter((_, idx) => idx !== i) }));
  const addWord = () => { if (!newWord) return; setProfile(p => ({ ...p, typingWords: [...(p.typingWords || []), newWord] })); setNewWord(''); };
  const removeWord = (i) => setProfile(p => ({ ...p, typingWords: p.typingWords.filter((_, idx) => idx !== i) }));
  const updateStat = (i, field, val) => { const s = [...(profile.stats || [])]; s[i] = { ...s[i], [field]: val }; setProfile(p => ({ ...p, stats: s })); };

  if (!profile) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: '#6a6a88', gap: 10 }}><Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />Memuat data profil...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontWeight: 700, fontSize: 22, color: '#f2f2ff', margin: '0 0 4px', letterSpacing: '-0.3px' }}>Profil & Hero</h1>
          <p style={{ fontSize: 13, color: '#6a6a88', margin: 0 }}>Kelola informasi pribadi dan konten hero</p>
        </div>
        <button onClick={save} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', borderRadius: 3, background: 'linear-gradient(135deg, #00e5ff, #9b00ff)', color: '#fff', fontWeight: 700, fontSize: 14, border: 'none', cursor: saving ? 'not-allowed' : 'pointer', boxShadow: 'none', opacity: saving ? 0.7 : 1 }}>
          {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : null} {saving ? 'Menyimpan...' : 'Simpan Semua'}
        </button>
      </div>

      <Tabs defaultValue='personal'>
        <TabsList style={{ background: 'var(--card)', border: '1px solid rgba(255,255,255,0.07)', padding: 4, borderRadius: 3, marginBottom: 24, display: 'inline-flex', gap: 2 }}>
          {['personal','hero','about','experience'].map(tab => (
            <TabsTrigger key={tab} value={tab} style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13, borderRadius: 9, padding: '8px 18px', color: '#6a6a88' }}>
              {{ personal: 'Info Pribadi', hero: 'Hero Section', about: 'Tentang', experience: 'Pengalaman' }[tab]}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value='personal'>
          <div style={sectionStyle}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[
                ['name','Nama Lengkap','Erwin Paisal'],['title','Jabatan / Title','IT Professional'],
                ['tagline','Tagline','Solusi teknologi...'],['location','Lokasi','Medan...'],
                ['email','Email','erwin@erwinpaisal.id'],['phone','Telepon','+62 812-...'],
                ['whatsapp','WhatsApp (angka)','628123...'],['linkedin','LinkedIn URL','https://linkedin.com/in/...'],
                ['photoUrl','URL Foto Profil','https://...'],
              ].map(([field, label, ph]) => (
                <div key={field} style={field === 'tagline' || field === 'photoUrl' ? { gridColumn: '1/-1' } : {}}>
                  <Label style={labelStyle}>{label}</Label>
                  <Input value={profile[field] || ''} onChange={e => setProfile(p => ({ ...p, [field]: e.target.value }))} placeholder={ph} style={inputStyle} />
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value='hero'>
          <div style={sectionStyle}>
            <h3 style={{ fontWeight: 700, fontSize: 15, color: '#f2f2ff', marginBottom: 16 }}>Statistik Hero</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }}>
              {(profile.stats || []).map((stat, i) => (
                <div key={i} style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 3, padding: 14 }}>
                  <Input value={stat.value} onChange={e => updateStat(i, 'value', e.target.value)} placeholder='5+' style={{ ...inputStyle, marginBottom: 8, textAlign: 'center', fontWeight: 700 }} />
                  <Input value={stat.label} onChange={e => updateStat(i, 'label', e.target.value)} placeholder='Label' style={{ ...inputStyle, fontSize: 12 }} />
                </div>
              ))}
            </div>
            <h3 style={{ fontWeight: 700, fontSize: 15, color: '#f2f2ff', marginBottom: 12 }}>Kata Animasi Typing</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {(profile.typingWords || []).map((word, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'rgba(0,229,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}>
                  <span style={{ fontSize: 13, color: '#00e5ff' }}>{word}</span>
                  <button onClick={() => removeWord(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6a6a88', padding: 0 }}><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Input value={newWord} onChange={e => setNewWord(e.target.value)} placeholder='Tambah kata baru...' style={{ ...inputStyle, flex: 1 }} onKeyDown={e => e.key === 'Enter' && addWord()} />
              <button onClick={addWord} style={{ padding: '8px 16px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(0,229,255,0.2)', color: '#00e5ff', cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}><Plus size={14} /> Tambah</button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value='about'>
          <div style={sectionStyle}>
            <h3 style={{ fontWeight: 700, fontSize: 15, color: '#f2f2ff', marginBottom: 16 }}>Paragraf Bio</h3>
            {(profile.bio || []).map((para, i) => (
              <div key={i} style={{ marginBottom: 12 }}>
                <Label style={labelStyle}>Paragraf {i + 1}</Label>
                <Textarea value={para} onChange={e => { const bio = [...profile.bio]; bio[i] = e.target.value; setProfile(p => ({ ...p, bio })); }} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
            ))}
            <h3 style={{ fontWeight: 700, fontSize: 15, color: '#f2f2ff', margin: '24px 0 12px' }}>Keunggulan</h3>
            {(profile.strengths || []).map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <Input value={s} onChange={e => { const arr = [...profile.strengths]; arr[i] = e.target.value; setProfile(p => ({ ...p, strengths: arr })); }} style={{ ...inputStyle, flex: 1 }} />
                <button onClick={() => removeStrength(i)} style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={14} /></button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <Input value={newStrength} onChange={e => setNewStrength(e.target.value)} placeholder='Tambah keunggulan...' style={{ ...inputStyle, flex: 1 }} onKeyDown={e => e.key === 'Enter' && addStrength()} />
              <button onClick={addStrength} style={{ padding: '8px 16px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(0,229,255,0.2)', color: '#00e5ff', cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}><Plus size={14} /> Tambah</button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value='experience'>
          <div style={sectionStyle}>
            <h3 style={{ fontWeight: 700, fontSize: 14, color: '#f2f2ff', marginBottom: 16, fontFamily: "'Space Grotesk', sans-serif" }}>Pengalaman Kerja</h3>
            {(profile.experience || []).map((exp, i) => (
              <div key={i} style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 3, padding: 16, marginBottom: 12, position: 'relative' }}>
                <button onClick={() => removeExperience(i)} style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer', padding: '4px 8px', borderRadius: 6 }}><Trash2 size={14} /></button>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div><Label style={labelStyle}>Jabatan</Label><Input value={exp.role} onChange={e => { const arr = [...profile.experience]; arr[i].role = e.target.value; setProfile(p => ({ ...p, experience: arr })); }} style={inputStyle} /></div>
                  <div><Label style={labelStyle}>Perusahaan</Label><Input value={exp.company} onChange={e => { const arr = [...profile.experience]; arr[i].company = e.target.value; setProfile(p => ({ ...p, experience: arr })); }} style={inputStyle} /></div>
                  <div><Label style={labelStyle}>Periode</Label><Input value={exp.period} onChange={e => { const arr = [...profile.experience]; arr[i].period = e.target.value; setProfile(p => ({ ...p, experience: arr })); }} style={inputStyle} /></div>
                </div>
                <Label style={labelStyle}>Deskripsi</Label>
                <Textarea value={exp.description} onChange={e => { const arr = [...profile.experience]; arr[i].description = e.target.value; setProfile(p => ({ ...p, experience: arr })); }} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
            ))}
            <div style={{ background: 'rgba(168,85,247,0.05)', border: '1px dashed rgba(168,85,247,0.25)', borderRadius: 3, padding: 16 }}>
              <h4 style={{ fontWeight: 700, fontSize: 13, color: '#00e5ff', marginBottom: 12 }}>+ Tambah Pengalaman</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div><Label style={labelStyle}>Jabatan</Label><Input value={newExp.role} onChange={e => setNewExp(p => ({ ...p, role: e.target.value }))} placeholder='IT Support Specialist' style={inputStyle} /></div>
                <div><Label style={labelStyle}>Perusahaan</Label><Input value={newExp.company} onChange={e => setNewExp(p => ({ ...p, company: e.target.value }))} placeholder='PT. Nama Perusahaan' style={inputStyle} /></div>
                <div><Label style={labelStyle}>Periode</Label><Input value={newExp.period} onChange={e => setNewExp(p => ({ ...p, period: e.target.value }))} placeholder='2021 - Sekarang' style={inputStyle} /></div>
              </div>
              <Label style={labelStyle}>Deskripsi</Label>
              <Textarea value={newExp.description} onChange={e => setNewExp(p => ({ ...p, description: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical', marginBottom: 12 }} />
              <button onClick={addExperience} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 9, background: 'linear-gradient(135deg, #00e5ff, #9b00ff)', color: '#fff', fontWeight: 600, fontSize: 13, border: 'none', cursor: 'pointer', boxShadow: '0 0 16px rgba(0,229,255,0.2)' }}><Plus size={14} /> Tambah</button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
